import { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { IEmpresas } from '../../interfaces/IEmpresas';
import EmpresaConciliacaoConfigService, { IEmpresaConciliacaoConfig } from '../../services/EmpresaConciliacaoConfigService';
import EmpresasService from '../../services/EmpresasService';

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

const TIPOS_CONCILIACAO = [
    { valor: 'FOLHA', descricao: 'Folha de Pagamento' }
];

function ConciliacaoConfigModal({ empresa, abaSelecionada }: { empresa: IEmpresas, abaSelecionada: string }) {
    const { t } = useTranslation();
    const [configs, setConfigs] = useState<IEmpresaConciliacaoConfig[]>([]);
    const [loading, setLoading] = useState(false);
    const [salvando, setSalvando] = useState<string | null>(null);
    const [folhaLiquido, setFolhaLiquido] = useState(false);
    const [salvandoFolhaLiquido, setSalvandoFolhaLiquido] = useState(false);

    const carregar = useCallback(() => {
        if (!empresa?.id) return;
        setLoading(true);
        EmpresaConciliacaoConfigService.listarPorEmpresa(empresa.id)
            .then(setConfigs)
            .catch(() => Swal.fire({ text: t('Não foi possível carregar as configurações de conciliação.'), icon: 'error', timer: 3000, showConfirmButton: false }))
            .finally(() => setLoading(false));

        EmpresasService.obterContabilizacaoFolha(empresa.id)
            .then(setFolhaLiquido)
            .catch(() => { /* mantém o valor atual; o switch continua utilizável */ });
    }, [empresa?.id, t]);

    useEffect(() => {
        if (abaSelecionada === '4') carregar();
    }, [abaSelecionada, carregar]);

    const alternarFolhaLiquido = useCallback(async (valor: boolean) => {
        if (!empresa?.id) return;
        setSalvandoFolhaLiquido(true);
        try {
            await EmpresasService.salvarContabilizacaoFolha(empresa.id, valor);
            setFolhaLiquido(valor);
            Swal.fire({ text: t('Configuração de conciliação atualizada.'), icon: 'success', timer: 2000, showConfirmButton: false });
        } catch {
            Swal.fire({ text: t('Não foi possível salvar a configuração de conciliação.'), icon: 'error', timer: 3000, showConfirmButton: false });
        } finally {
            setSalvandoFolhaLiquido(false);
        }
    }, [empresa?.id, t]);

    const configAtiva = (tipo: string) => configs.find(c => c.tipoConciliacao === tipo && c.ativo);

    const alternarModo = useCallback(async (tipo: string, llmAtivo: boolean) => {
        if (!empresa?.id) return;
        const novoModo = llmAtivo ? 'LLM' : 'DETERMINISTICO';
        setSalvando(tipo);

        try {
            const existente = configAtiva(tipo);
            const request = {
                empresaId: empresa.id,
                tipoConciliacao: tipo,
                modo: novoModo,
                vigenciaInicio: existente?.vigenciaInicio ?? new Date().toISOString().slice(0, 10),
                vigenciaFim: null
            };

            if (existente) {
                await EmpresaConciliacaoConfigService.atualizar(existente.id, request);
            } else {
                await EmpresaConciliacaoConfigService.criar(request);
            }

            Swal.fire({ text: t('Configuração de conciliação atualizada.'), icon: 'success', timer: 2000, showConfirmButton: false });
            carregar();
        } catch {
            Swal.fire({ text: t('Não foi possível salvar a configuração de conciliação.'), icon: 'error', timer: 3000, showConfirmButton: false });
        } finally {
            setSalvando(null);
        }
    }, [empresa?.id, configs, carregar, t]);

    return (
        <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '10px', minHeight: '505px', display: abaSelecionada === '4' ? 'block' : 'none' }}>
            <div className="row" style={{ width: '100%' }}>
                <div className="col-md-12 mb-3">
                    <p className="text-muted" style={{ fontSize: 13 }}>
                        {t('Escolha, por tipo de conciliação, se o processamento (leitura do arquivo e sugestão de conciliação) deve rodar pelo motor determinístico ou pelo assistente de IA.')}
                    </p>
                </div>

                <div className="col-md-12 mb-3" style={{ border: '1px solid #eee', borderRadius: 5, padding: 12 }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div className="fw-bold" style={{ fontSize: 14 }}>{t('Contabiliza a folha pelo valor líquido')}</div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                                {t('Na capa da FOPAG, as baixas da conta de "Salários a Pagar" passam a ser reconhecidas como Pagamento da Folha, em vez de Desconto. Use quando a empresa provisiona a folha já pelo líquido (INSS/FGTS/IRRF em contas próprias).')}
                            </div>
                        </div>
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={folhaLiquido}
                                disabled={salvandoFolhaLiquido}
                                onChange={(e) => alternarFolhaLiquido(e.target.checked)}
                            />
                            <label className="form-check-label" style={{ fontSize: 12 }}>
                                {salvandoFolhaLiquido ? t('Salvando...') : (folhaLiquido ? t('Sim') : t('Não'))}
                            </label>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="col-md-12 d-flex justify-content-center py-4">
                        <span className="spinner-border spinner-border-sm text-primary me-2 mt-1" />
                        <span>{t('Carregando...')}</span>
                    </div>
                ) : (
                    TIPOS_CONCILIACAO.map(tipo => {
                        const config = configAtiva(tipo.valor);
                        const llmAtivo = config?.modo === 'LLM';
                        return (
                            <div key={tipo.valor} className="col-md-12 mb-3" style={{ border: '1px solid #eee', borderRadius: 5, padding: 12 }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="fw-bold" style={{ fontSize: 14 }}>{t(tipo.descricao)}</div>
                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                            {llmAtivo ? t('Conciliação via IA (Claude) — leitura do arquivo e sugestões geradas pelo assistente.') : t('Motor determinístico — leitura por layout configurado e soma exata/combinação.')}
                                        </div>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            checked={llmAtivo}
                                            disabled={salvando === tipo.valor}
                                            onChange={(e) => alternarModo(tipo.valor, e.target.checked)}
                                        />
                                        <label className="form-check-label" style={{ fontSize: 12 }}>
                                            {salvando === tipo.valor ? t('Salvando...') : (llmAtivo ? t('IA') : t('Determinístico'))}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default ConciliacaoConfigModal;
